import { Injectable, inject } from '@angular/core';
import { ChatMessage } from '../../../core/models/chat-message';
import { RagService } from '../../rag/services/rag.service';
import { LmStudioService } from '../../../core/services/lm-studio.service';
import { HelperModelService, QueryAnalysis } from './helper-model.service';

export type QueryRoute = 'local' | 'rag' | 'web';

export type RouteDecision = {
  route: QueryRoute;
  reason: string;
  retrievalQuery?: string;
  /** Identifies the model (or heuristic) that produced the decision, for logging. */
  model?: string;
};

const ROUTER_SYSTEM_PROMPT = `You are a routing agent for a private assistant. Choose exactly one route:
- "rag": the answer should rely on the user's indexed private documents.
- "web": the answer needs current, externally verifiable, or explicitly requested internet information.
- "local": use the local model's general knowledge, reasoning, drafting, or conversation only.

Use rag only when the private documents are likely relevant. Use web for news, current events, current prices, schedules, live facts, links, or explicit requests to search/browse/check online. Do not use web for timeless reasoning or writing. Return only valid JSON: {"route":"local|rag|web","reason":"brief reason"}.`;

@Injectable({ providedIn: 'root' })
export class QueryRouterService {
  private readonly llm = inject(LmStudioService);
  private readonly rag = inject(RagService);
  private readonly helper = inject(HelperModelService);

  async decide(messages: ChatMessage[]): Promise<RouteDecision> {
    const latestUser = [...messages].reverse().find((message) => message.author === 'user');
    const query = latestUser?.text.trim();
    // The image is already in the conversation; a local vision model can analyze
    // it directly, so skip the helper model and retrieval entirely.
    if (latestUser?.attachments?.some((attachment) => attachment.dataUrl)) {
      return { route: 'local', reason: 'The user attached an image; the local vision model analyzes it directly.', model: 'heuristic' };
    }
    if (!query) return { route: 'local', reason: 'No user query was available.', model: 'none' };

    const indexedDocuments = this.rag.documents()
      .filter((document) => document.status === 'indexed')
      .map((document) => document.name);

    // Deterministic requests should not pay for (or depend on) a helper-model
    // call. In particular, ordinary conversational/definition questions are
    // local tasks, while explicit web and document requests are unambiguous.
    const preferredRoute = this.preferredRoute(query, indexedDocuments.length > 0);
    if (preferredRoute) return { ...preferredRoute, model: 'heuristic' };

    if (this.helper.isEnabled()) {
      try {
        const decision = await this.decideWithHelper(query, indexedDocuments.length > 0);
        return { ...decision, model: this.llm.helperModelId() ?? 'helper' };
      } catch (error) {
        // The helper is intentionally non-critical. Preserve the current routing path
        // if it times out, is unavailable, or does not produce valid JSON.
        console.warn('[HelperModel] Query analysis failed; using the standard router.', error);
      }
    }

    const context = messages.slice(-6).map((message) => `${message.author}: ${message.text}`).join('\n');
    const prompt = `Indexed private documents (${indexedDocuments.length}): ${indexedDocuments.join(', ') || 'none'}\n\nConversation:\n${context}\n\nDecide the route for the latest user query.`;

    try {
      const answer = await this.llm.chat(
        [{ author: 'user', text: prompt, time: '' }],
        // Local reasoning models can consume a small token budget before they
        // emit the route JSON, so leave room for a real final answer. Routing is
        // a lightweight task, so use the helper model when one is configured.
        { systemPrompt: ROUTER_SYSTEM_PROMPT, maxTokens: 1024, model: this.llm.helperModelId(), useHelperApi: true },
      );
      return { ...this.parse(answer), model: this.llm.helperModelId() ?? 'auto' };
    } catch {
      return { ...this.heuristic(query, indexedDocuments.length > 0), model: 'heuristic' };
    }
  }

  private async decideWithHelper(query: string, hasDocuments: boolean): Promise<RouteDecision> {
    const analysis = await this.helper.analyzeQuery(query);
    const decision = this.routeFromAnalysis(analysis, hasDocuments);
    console.debug('[HelperModel] Route selected', {
      intent: analysis.intent,
      route: decision.route,
      web: analysis.useWeb,
      rag: analysis.useRag,
    });
    return decision;
  }

  private routeFromAnalysis(analysis: QueryAnalysis, hasDocuments: boolean): RouteDecision {
    const ragQuery = analysis.ragQueries.find((query) => query.trim());
    const webQuery = analysis.searchQueries.find((query) => query.trim());
    // RAG remains first so its established relevance check and web fallback still apply.
    if (analysis.useRag && hasDocuments) {
      return { route: 'rag', reason: `Helper selected private-document retrieval (${analysis.intent}).`, retrievalQuery: ragQuery };
    }
    if (analysis.useWeb) {
      return { route: 'web', reason: `Helper selected web retrieval (${analysis.intent}).`, retrievalQuery: webQuery };
    }
    return { route: 'local', reason: `Helper selected local reasoning (${analysis.intent}).` };
  }

  private parse(answer: string): RouteDecision {
    const json = answer.match(/\{[\s\S]*\}/)?.[0];
    if (!json) throw new Error('Router did not return JSON.');
    const parsed = JSON.parse(json) as Partial<RouteDecision>;
    if (parsed.route !== 'local' && parsed.route !== 'rag' && parsed.route !== 'web') {
      throw new Error('Router returned an unsupported route.');
    }
    return { route: parsed.route, reason: typeof parsed.reason === 'string' ? parsed.reason : '' };
  }

  private heuristic(query: string, hasDocuments: boolean): RouteDecision {
    const preferredRoute = this.preferredRoute(query, hasDocuments);
    if (preferredRoute) return preferredRoute;
    return { route: 'local', reason: 'The request can be answered without retrieval.' };
  }

  private preferredRoute(query: string, hasDocuments: boolean): RouteDecision | undefined {
    const normalized = query.trim().replace(/\s+/g, ' ');
    const explicitWeb = /\b(search|browse|look up|google|online|internet|web search|check online)\b/i.test(normalized);
    const timeSensitive = /\b(latest|today|current|news|price|weather|score|schedule|availability|release date|recent)\b/i.test(normalized);
    if (explicitWeb || timeSensitive) {
      return { route: 'web', reason: explicitWeb ? 'The user explicitly requested online research.' : 'The request needs current online information.' };
    }

    const documentRequest = /\b(document|documents|file|files|uploaded|attachment|our notes|my notes|according to)\b/i.test(normalized);
    if (hasDocuments && documentRequest) {
      return { route: 'rag', reason: 'The request refers to private documents.' };
    }

    const conversational = /^(?:(?:hi|hello|hey)[,!.]?\s*)?(?:what(?:'s| is)|what does)\b/i.test(normalized)
      || /^(?:hi|hello|hey|thanks|thank you|good (?:morning|afternoon|evening))[!.?]*$/i.test(normalized);
    if (conversational) {
      return { route: 'local', reason: 'The request is a general conversational or definition question.' };
    }

    const localTask = /^(?:write|draft|rewrite|summari[sz]e|translate|brainstorm|create|make|calculate|solve|proofread|improve|roleplay)\b/i.test(normalized)
      || /\b(?:this chat|our conversation|you just said|i just said)\b/i.test(normalized);
    const researchQuestion = /\b(?:best|recommend|compare|difference|alternatives?|reviews?|statistics?|facts?)\b/i.test(normalized);
    if (!localTask && researchQuestion) {
      return { route: 'web', reason: 'The request is an information-seeking question; web sources are preferred.' };
    }

    return undefined;
  }
}
